/**
 * Dedicated product page — URL: /produit/:id
 * Reuses ProductDetailSheet content in a full-page layout.
 * Better for SEO, pixel tracking (URL-based conversions), social sharing.
 * Injects Schema.org Product + AggregateRating JSON-LD for Google rich snippets.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useLang, useSettings, cldUrl } from "../lib/providers";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppFloater from "../components/WhatsAppFloater";
import ProductDetailSheet from "../components/ProductDetailSheet";
import ProductReviewsBlock from "../components/ProductReviewsBlock";
import ReassuranceBlock from "../components/ReassuranceBlock";
import { ArrowLeft } from "lucide-react";
import { firePixelEvent } from "../components/TrackingScripts";
import { goBack } from "../lib/navHelpers";

// Inject (and clean up) a JSON-LD <script> with the given payload
function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
    return () => { try { script && script.remove(); } catch { /* ignore */ } };
  }, [id, data]);
}

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const { settings } = useSettings();
  const [product, setProduct] = useState(null);
  const [reviewStats, setReviewStats] = useState(null); // {count, avg, dist}
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/products`);
        const found = (data || []).find((p) => p.id === id);
        if (cancelled) return;
        setProduct(found || null);
        if (found) {
          // Update tab title + meta description for SEO & sharing
          document.title = `${found.name} — ${settings.site_name || "DZ Tennis Shopper"}`;
          const desc = found.description || found.name;
          const ensureMeta = (attr, key, value) => {
            let m = document.querySelector(`meta[${attr}="${key}"]`);
            if (!m) { m = document.createElement("meta"); m.setAttribute(attr, key); document.head.appendChild(m); }
            m.setAttribute("content", value);
          };
          ensureMeta("name", "description", desc);
          // Product image (Cloudinary if available)
          const imgs = (found.images && found.images.length > 0) ? found.images : (found.image_url ? [found.image_url] : []);
          const ogImg = imgs[0] ? cldUrl(imgs[0], { w: 1200 }) : "";
          // Open Graph + Twitter tags for social share (WhatsApp / FB / TikTok / X)
          ensureMeta("property", "og:type", "product");
          ensureMeta("property", "og:title", `${found.name} — ${settings.site_name || "DZ Tennis Shopper"}`);
          ensureMeta("property", "og:description", desc);
          ensureMeta("property", "og:url", window.location.href);
          ensureMeta("property", "og:site_name", settings.site_name || "DZ Tennis Shop");
          if (ogImg) ensureMeta("property", "og:image", ogImg);
          ensureMeta("name", "twitter:card", "summary_large_image");
          ensureMeta("name", "twitter:title", `${found.name} — ${settings.site_name || "DZ Tennis Shopper"}`);
          ensureMeta("name", "twitter:description", desc);
          if (ogImg) ensureMeta("name", "twitter:image", ogImg);
          // Canonical
          let canon = document.querySelector('link[rel="canonical"]');
          if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
          canon.setAttribute("href", `${window.location.origin}/produit/${found.id}`);
          firePixelEvent("ViewContent", {
            value: found.price_eur || 0,
            currency: "EUR",
            content_ids: [found.id],
            content_name: found.name,
            content_type: "product",
            content_category: found.category,
          });
        }
      } catch {
        if (!cancelled) setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Fetch published reviews for this product for the AggregateRating + reviews block
    axios.get(`${API}/reviews/public/by-product/${id}`)
      .then((r) => { if (!cancelled) setReviewStats(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id, settings.site_name]);

  // Build Schema.org Product JSON-LD (with AggregateRating when reviews exist)
  const ldData = (() => {
    if (!product) return null;
    const rate = settings.exchange_rate || 280;
    const priceEur = product.price_eur || 0;
    const dzd = product.price_dzd > 0 ? product.price_dzd : Math.round(priceEur * rate);
    const imgs = (product.images && product.images.length > 0)
      ? product.images
      : (product.image_url ? [product.image_url] : []);
    const availability = product.stock_status === "out_of_stock"
      ? "https://schema.org/OutOfStock"
      : (product.stock_status === "on_order" ? "https://schema.org/PreOrder" : "https://schema.org/InStock");
    const data = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.name,
      "description": product.description || product.name,
      "brand": product.brand ? { "@type": "Brand", "name": product.brand } : undefined,
      "image": imgs.map((u) => cldUrl(u, { w: 1200 })).filter(Boolean),
      "sku": product.id,
      "offers": {
        "@type": "Offer",
        "url": typeof window !== "undefined" ? window.location.href : undefined,
        "priceCurrency": "DZD",
        "price": dzd,
        "availability": availability,
        "itemCondition": "https://schema.org/NewCondition",
      },
    };
    if (reviewStats && reviewStats.count > 0) {
      data.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": Number((reviewStats.avg || 0).toFixed(2)),
        "reviewCount": reviewStats.count,
        "bestRating": 5,
        "worstRating": 1,
      };
      // Individual reviews (up to first 5 published) for richer snippets
      const sample = (reviewStats.data || []).slice(0, 5).filter((r) => r.comment);
      if (sample.length > 0) {
        data.review = sample.map((r) => ({
          "@type": "Review",
          "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5 },
          "author": { "@type": "Person", "name": r.customer_name || "Client" },
          "reviewBody": r.comment,
          "datePublished": (r.created_at || "").slice(0, 10) || undefined,
        }));
      }
    }
    return data;
  })();

  useJsonLd(`ld-product-${id}`, ldData);

  // Breadcrumb JSON-LD (Accueil > Produits > Produit)
  const ldBreadcrumb = (() => {
    if (!product) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": lang === "ar" ? "الرئيسية" : "Accueil", "item": `${origin}/` },
        { "@type": "ListItem", "position": 2, "name": lang === "ar" ? "المنتجات" : "Produits", "item": `${origin}/#produits` },
        { "@type": "ListItem", "position": 3, "name": product.name, "item": `${origin}/produit/${product.id}` },
      ],
    };
  })();
  useJsonLd(`ld-breadcrumb-${id}`, ldBreadcrumb);

  // When user closes the sheet → stay on the page (product summary is visible behind)
  const handleSheetChange = (open) => {
    setSheetOpen(open);
  };

  return (
    <div className="min-h-screen text-white overflow-x-hidden" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header />

      <main className="pt-12 pb-20 px-4">
        <div className="max-w-5xl mx-auto">
          <button type="button" onClick={() => goBack(navigate)} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-neon mb-6 transition-colors" data-testid="product-page-back">
            <ArrowLeft className="w-4 h-4" />
            {lang === "ar" ? "رجوع" : "Retour"}
          </button>

          {loading ? (
            <div className="text-center text-white/50 py-16">
              {lang === "ar" ? "جاري التحميل..." : "Chargement du produit..."}
            </div>
          ) : !product ? (
            <div className="text-center py-16">
              <p className="text-clay text-lg mb-4">
                {lang === "ar" ? "لم يتم العثور على المنتج" : "Produit introuvable"}
              </p>
              <Link to="/" className="text-neon underline">
                {lang === "ar" ? "العودة" : "Retour à l'accueil"}
              </Link>
            </div>
          ) : (
            <div data-testid="product-page-summary">
              {/* Fil d'Ariane visible */}
              <nav className="text-[11px] uppercase tracking-widest text-white/40 mb-6 flex items-center gap-2 flex-wrap" data-testid="product-breadcrumb">
                <Link to="/" className="hover:text-neon transition-colors">{lang === "ar" ? "الرئيسية" : "Accueil"}</Link>
                <span>/</span>
                <Link to="/#produits" className="hover:text-neon transition-colors">{lang === "ar" ? "المنتجات" : "Produits"}</Link>
                <span>/</span>
                <span className="text-white/70">{product.name}</span>
              </nav>
              {/* Résumé produit visible derrière / après fermeture du panneau */}
              <div className="grid md:grid-cols-2 gap-8 items-start mb-10">
                <div className="border border-white/10 bg-white/5 p-6 flex items-center justify-center relative">
                  {product.is_new && (
                    <div className="absolute top-3 start-3 px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-neon text-black border border-black/10 shadow-lg" data-testid="seo-page-new-badge">
                      {lang === "ar" ? "جديد" : "Nouveau"}
                    </div>
                  )}
                  {(product.images?.[0] || product.image_url) ? (
                    <img
                      src={cldUrl(product.images?.[0] || product.image_url, { w: 800 })}
                      alt={product.name}
                      className="max-h-[380px] object-contain"
                    />
                  ) : (
                    <div className="text-white/30 py-24">🎾</div>
                  )}
                </div>
                <div>
                  {product.brand && <p className="text-[11px] uppercase tracking-widest text-neon mb-2">{product.brand}</p>}
                  <h1 className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl mb-4">{product.name}</h1>
                  <p className="font-heading font-black text-neon text-3xl mb-4">
                    {Math.round(product.price_dzd > 0 ? product.price_dzd : (product.price_eur || 0) * (settings.exchange_rate || 280)).toLocaleString("fr-FR")} DZD
                  </p>
                  {(lang === "ar" ? product.description_ar : product.description) && (
                    <p className="text-sm text-white/70 leading-relaxed mb-6 line-clamp-6">
                      {lang === "ar" ? (product.description_ar || product.description) : product.description}
                    </p>
                  )}
                  <button
                    onClick={() => setSheetOpen(true)}
                    className="bg-neon text-black font-black uppercase tracking-widest text-xs px-8 py-4 hover:opacity-90 transition-opacity"
                    data-testid="product-page-open-sheet"
                  >
                    {lang === "ar" ? "🛒 اطلب الآن — التفاصيل الكاملة" : "🛒 Commander — Voir le détail complet"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reviews block for this product — visible on the dedicated page */}
          {product && reviewStats && reviewStats.count > 0 && (
            <ProductReviewsBlock stats={reviewStats} />
          )}
        </div>
      </main>

      <ReassuranceBlock variant="lines" compact />

      {/* Reuse existing ProductDetailSheet — opens automatically on page load */}
      {product && (
        <ProductDetailSheet product={product} open={sheetOpen} onOpenChange={handleSheetChange} />
      )}

      <Footer onAdminClick={() => {}} />
      <WhatsAppFloater />
    </div>
  );
}
